/**
 * @module IdleTimeoutProvider
 *
 * Orchestration layer that connects the idle timeout hook to the
 * warning modal and the auth system. Wraps the authenticated area
 * of the app and handles the full lifecycle:
 *
 *   1. Monitors user inactivity via useIdleTimeout
 *   2. Shows IdleTimeoutModal when prompted
 *   3. Signs user out and redirects on timeout
 *   4. Broadcasts logout to other tabs
 */

import { useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
import { IDLE_TIMEOUT_MS } from "../lib/sessionTimeout";
import { IdleTimeoutModal } from "./ui/IdleTimeoutModal";
import { logError } from "../lib/logError";
import { rememberReturnTarget } from "../lib/loginReturn";

// ─── Configuration ───────────────────────────────────────────

/** Time the warning is shown before auto-logout (60 seconds) */
const PROMPT_TIMEOUT_MS = 60 * 1000;

const PROMPT_TOTAL_SECONDS = Math.ceil(PROMPT_TIMEOUT_MS / 1000);

// ─── Component ───────────────────────────────────────────────

interface IdleTimeoutProviderProps {
  children: ReactNode;
}

export function IdleTimeoutProvider({ children }: IdleTimeoutProviderProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // ── Timeout Callback ──────────────────────────────────────

  const handleIdle = useCallback(async () => {
    try {
      // Broadcast logout to other tabs before signing out
      try {
        const channel = new BroadcastChannel("seq-claims-idle-sync");
        channel.postMessage({ type: "logout" });
        channel.close();
      } catch {
        // BroadcastChannel not supported
      }

      rememberReturnTarget(`${window.location.pathname}${window.location.search}${window.location.hash}`, true);
      await signOut({ keepReturnTarget: true });
      navigate("/login?session=expired", { replace: true });
    } catch (err) {
      logError("[IdleTimeout] Error during sign out:", err);
      // Force redirect even if signOut fails
      navigate("/login?session=expired", { replace: true });
    }
  }, [signOut, navigate]);

  // ── Hook ──────────────────────────────────────────────────

  const { isPrompted, remainingSeconds, reset } = useIdleTimeout({
    idleTimeout: IDLE_TIMEOUT_MS,
    promptTimeout: PROMPT_TIMEOUT_MS,
    onIdle: handleIdle,
    enabled: !!user, // Only track when authenticated
  });

  // ── Stay Signed In Handler ────────────────────────────────

  const handleStaySignedIn = useCallback(() => {
    reset();
  }, [reset]);

  // ── Sign Out Now Handler ──────────────────────────────────

  const handleSignOutNow = useCallback(async () => {
    try {
      await signOut();
      navigate("/login", { replace: true });
    } catch {
      navigate("/login", { replace: true });
    }
  }, [signOut, navigate]);

  return (
    <>
      {children}
      <IdleTimeoutModal
        isOpen={isPrompted}
        remainingSeconds={remainingSeconds}
        totalSeconds={PROMPT_TOTAL_SECONDS}
        onStaySignedIn={handleStaySignedIn}
        onSignOut={handleSignOutNow}
      />
    </>
  );
}
